import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const TIER_NAMES = {
  free_trial: 'Free Trial',
  starter: 'Starter',
  pro: 'PRO',
  enterprise: 'Enterprise'
};

function AdminBilling() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [cardDetails, setCardDetails] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const submitLockRef = useRef(false);

  // Square SDK state
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [card, setCard] = useState(null);
  const cardContainerRef = useRef(null);
  const paymentsRef = useRef(null);

  // Square configuration
  const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
  const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
  const isProduction = process.env.REACT_APP_SQUARE_ENVIRONMENT === 'production';

  useEffect(() => {
    fetchBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Square SDK
  useEffect(() => {
    if (showPaymentModal || showCardModal) {
      loadSquareSDK();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentModal, showCardModal]);

  const loadSquareSDK = () => {
    if (window.Square) {
      setSquareLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = isProduction
      ? 'https://web.squarecdn.com/v1/square.js'
      : 'https://sandbox.web.squarecdn.com/v1/square.js';
    script.async = true;

    script.onload = () => setSquareLoaded(true);
    script.onerror = () => setError('Failed to load payment system');

    document.body.appendChild(script);
  };

  // Initialize Square Card when modal opens and SDK is loaded
  const initializeCard = useCallback(async () => {
    if (!squareLoaded || !window.Square || !cardContainerRef.current || card) {
      return;
    }

    try {
      const payments = await window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
      paymentsRef.current = payments;

      const cardInstance = await payments.card();
      await cardInstance.attach(cardContainerRef.current);
      setCard(cardInstance);
    } catch (err) {
      console.error('Failed to initialize Square card:', err);
      setError('Failed to initialize payment form');
    }
  }, [squareLoaded, card, SQUARE_APP_ID, SQUARE_LOCATION_ID]);

  useEffect(() => {
    if (squareLoaded && (showPaymentModal || showCardModal) && !card) {
      // Small delay to ensure container is rendered
      const timer = setTimeout(initializeCard, 100);
      return () => clearTimeout(timer);
    }
  }, [squareLoaded, showPaymentModal, showCardModal, card, initializeCard]);

  // Clean up card when modal closes
  useEffect(() => {
    if (!showPaymentModal && !showCardModal && card) {
      card.destroy();
      setCard(null);
    }
  }, [showPaymentModal, showCardModal, card]);

  const fetchBillingData = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      // Fetch billing status
      const statusRes = await fetch(`${process.env.REACT_APP_API_URL}/admin/billing-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!statusRes.ok) throw new Error('Failed to load billing status');
      const statusData = await statusRes.json();
      console.log('[AdminBilling] Status data:', statusData);
      console.log('[AdminBilling] hasPaymentMethod:', statusData.hasPaymentMethod);
      setBilling(statusData);

      // Fetch payment history
      const historyRes = await fetch(`${process.env.REACT_APP_API_URL}/billing/payment-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setPaymentHistory(historyData.payments || []);
      }

      // Fetch card details if has payment method
      if (statusData.hasPaymentMethod) {
        console.log('[AdminBilling] Fetching card details...');
        const cardRes = await fetch(`${process.env.REACT_APP_API_URL}/billing/card-details`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (cardRes.ok) {
          const cardData = await cardRes.json();
          console.log('[AdminBilling] Card data:', cardData);
          setCardDetails(cardData.card);
        } else {
          console.log('[AdminBilling] Card fetch failed:', cardRes.status);
        }
      } else {
        console.log('[AdminBilling] No payment method, skipping card fetch');
      }

    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoBilling = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const newValue = !billing.autoBilling;

      // If enabling and no payment method, show card modal
      if (newValue && !billing.hasPaymentMethod) {
        setShowCardModal(true);
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/billing/toggle-auto-billing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: newValue })
      });

      if (res.ok) {
        setBilling({ ...billing, autoBilling: newValue });
        setMessage(newValue ? 'Auto-billing enabled' : 'Auto-billing disabled');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to update auto-billing');
      }
    } catch (err) {
      setError('Failed to update auto-billing');
    }
  };

  const handlePayNow = async () => {
    if (!card) {
      setError('Payment form not ready');
      return;
    }

    // Synchronous double-submit guard. `processing` only disables the button on
    // the next render, so two clicks dispatched before React re-renders would
    // both get through. A ref flips immediately.
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setProcessing(true);
    setError('');

    try {
      const result = await card.tokenize();

      if (result.status === 'OK') {
        const token = localStorage.getItem('adminToken');

        const res = await fetch(`${process.env.REACT_APP_API_URL}/billing/reactivate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            cardNonce: result.token,
            saveCard: billing.autoBilling
          })
        });

        const data = await res.json();

        if (res.ok && data.success) {
          setShowPaymentModal(false);
          setMessage('Payment successful! Your account has been reactivated.');
          fetchBillingData();
        } else {
          setError(data.error || 'Payment failed');
        }
      } else {
        let errorMessage = 'Card validation failed. ';
        if (result.errors) {
          errorMessage += result.errors.map(e => e.message).join(', ');
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
      submitLockRef.current = false;
    }
  };

  const handleUpdateCard = async () => {
    if (!card) {
      setError('Payment form not ready');
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setProcessing(true);
    setError('');

    try {
      const result = await card.tokenize();

      if (result.status === 'OK') {
        const token = localStorage.getItem('adminToken');

        const res = await fetch(`${process.env.REACT_APP_API_URL}/billing/update-payment-method`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ cardNonce: result.token })
        });

        const data = await res.json();

        if (res.ok && data.success) {
          setShowCardModal(false);
          setCardDetails(data.card);
          setBilling({ ...billing, hasPaymentMethod: true, autoBilling: true });
          setMessage('Payment method updated successfully');
          setTimeout(() => setMessage(''), 3000);
        } else {
          setError(data.error || 'Failed to update card');
        }
      } else {
        let errorMessage = 'Card validation failed. ';
        if (result.errors) {
          errorMessage += result.errors.map(e => e.message).join(', ');
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Update card error:', err);
      setError('Failed to update payment method');
    } finally {
      setProcessing(false);
      submitLockRef.current = false;
    }
  };

  const handleRemoveCard = async () => {
    if (!window.confirm('Remove your payment method? Auto-billing will be disabled.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');

      const res = await fetch(`${process.env.REACT_APP_API_URL}/billing/remove-payment-method`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setCardDetails(null);
        setBilling({ ...billing, hasPaymentMethod: false, autoBilling: false });
        setMessage('Payment method removed');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to remove payment method');
      }
    } catch (err) {
      setError('Failed to remove payment method');
    }
  };

  const handlePrintHistory = () => {
    window.print();
  };

  const handleDownloadReceipt = async (paymentId) => {
    const token = localStorage.getItem('adminToken');
    window.open(`${process.env.REACT_APP_API_URL}/billing/receipt/${paymentId}?token=${token}`, '_blank');
  };

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div className="loading-spinner"></div>
          <p style={{ color: 'var(--color-text-light)', marginTop: '15px' }}>Loading billing information...</p>
        </div>
      </div>
    );
  }

  const needsPayment = billing?.isSuspended || billing?.billingStatus === 'past_due';
  const statusColor = billing?.isSuspended ? '#dc2626' :
                      billing?.billingStatus === 'past_due' ? '#f59e0b' : '#22c55e';

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <div className="admin-hub-header">
        <button onClick={() => navigate('/admin/account-hub')} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">-</span>
          Back to Account
        </button>
        <h1 className="admin-page-title">Billing & Subscription</h1>
        <p className="admin-hub-subtitle">Manage your subscription, payment method, and view billing history</p>
      </div>

      {/* Messages */}
      {message && (
        <div style={{
          background: 'var(--color-success-bg)',
          color: 'var(--color-success-text)',
          padding: '15px 20px',
          borderRadius: '10px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>&#10003;</span>
          {message}
        </div>
      )}

      {error && (
        <div style={{
          background: 'var(--color-error-bg)',
          color: 'var(--color-error-text)',
          padding: '15px 20px',
          borderRadius: '10px',
          marginBottom: '20px'
        }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            x
          </button>
        </div>
      )}

      {/* Trial Expired Banner */}
      {(billing?.isTrialExpired || billing?.isSuspendedForTrial) && (
        <div style={{
          background: 'linear-gradient(135deg, #ea580c, #c2410c)',
          color: 'white',
          padding: '25px',
          borderRadius: '15px',
          marginBottom: '25px',
          textAlign: 'center'
        }}>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px' }}>Your Free Trial Has Expired</h2>
          <p style={{ margin: '0 0 10px', opacity: 0.9 }}>
            Your account is now in <strong>read-only mode</strong>. You can view your data but cannot create or edit records.
          </p>
          <p style={{ margin: '0 0 20px', opacity: 0.9 }}>
            Upgrade to a paid plan to restore full access and continue managing your properties.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.href = '/admin/pricing'}
              style={{
                background: 'var(--color-surface)',
                color: 'var(--color-warning-text)',
                border: 'none',
                padding: '15px 40px',
                borderRadius: '10px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              View Pricing Plans
            </button>
          </div>
          <p style={{ margin: '15px 0 0', fontSize: '14px', opacity: 0.8 }}>
            Your data is safe and will be preserved. Upgrade anytime to continue.
          </p>
        </div>
      )}

      {/* Suspended Banner (for payment issues, not trial) */}
      {billing?.isSuspended && !billing?.isSuspendedForTrial && (
        <div style={{
          background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
          color: 'white',
          padding: '25px',
          borderRadius: '15px',
          marginBottom: '25px',
          textAlign: 'center'
        }}>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px' }}>Account Suspended</h2>
          <p style={{ margin: '0 0 20px', opacity: 0.9 }}>
            Your account has been suspended due to non-payment. Pay now to restore access.
          </p>
          <button
            onClick={() => setShowPaymentModal(true)}
            style={{
              background: 'var(--color-surface)',
              color: 'var(--color-error)',
              border: 'none',
              padding: '15px 40px',
              borderRadius: '10px',
              fontSize: '18px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            Pay ${billing.totalDue?.toFixed(2)} & Reactivate
          </button>
        </div>
      )}

      {/* Trial Expiring Soon Warning */}
      {billing?.subscriptionTier === 'free_trial' &&
       !billing?.isTrialExpired &&
       !billing?.isSuspendedForTrial &&
       billing?.trialDaysRemaining !== null &&
       billing?.trialDaysRemaining <= 7 &&
       billing?.trialDaysRemaining > 0 && (
        <div style={{
          background: billing.trialDaysRemaining <= 3
            ? 'linear-gradient(135deg, #f59e0b, #d97706)'
            : 'linear-gradient(135deg, #3b82f6, #2563eb)',
          color: 'white',
          padding: '20px',
          borderRadius: '15px',
          marginBottom: '25px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div>
            <h3 style={{ margin: '0 0 5px', fontSize: '18px' }}>
              {billing.trialDaysRemaining <= 3 ? 'Your trial expires soon!' : 'Trial ending soon'}
            </h3>
            <p style={{ margin: 0, opacity: 0.9 }}>
              {billing.trialDaysRemaining === 1
                ? 'Your free trial expires tomorrow.'
                : `Your free trial expires in ${billing.trialDaysRemaining} days.`}
              {' '}Upgrade now to keep full access.
            </p>
          </div>
          <button
            onClick={() => window.location.href = '/admin/pricing'}
            style={{
              background: 'var(--color-surface)',
              color: billing.trialDaysRemaining <= 3 ? '#d97706' : '#2563eb',
              border: 'none',
              padding: '12px 30px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Upgrade Now
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
        {/* Subscription Overview */}
        <div className="card" style={{ padding: '25px' }}>
          <h2 style={{ margin: '0 0 20px', color: 'var(--color-brand-text)', fontSize: '18px' }}>
            Subscription Overview
          </h2>

          <div style={{ display: 'grid', gap: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--color-text-light)' }}>Current Plan</span>
              <span style={{
                background: 'var(--color-primary)',
                color: 'white',
                padding: '5px 15px',
                borderRadius: '20px',
                fontWeight: '600'
              }}>
                {TIER_NAMES[billing?.subscriptionTier] || 'Free Trial'}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--color-text-light)' }}>Monthly Rate</span>
              <span style={{ fontWeight: '600' }}>${billing?.monthlyRate?.toFixed(2) || '0.00'}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--color-text-light)' }}>Status</span>
              <span style={{
                background: `${statusColor}20`,
                color: statusColor,
                padding: '5px 15px',
                borderRadius: '20px',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {billing?.isSuspended ? 'Suspended' :
                 billing?.billingStatus === 'past_due' ? 'Past Due' : 'Current'}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--color-text-light)' }}>Next Payment</span>
              <span style={{ fontWeight: '600' }}>
                {billing?.nextPaymentDate
                  ? new Date(billing.nextPaymentDate).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric'
                    })
                  : 'N/A'}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--color-text-light)' }}>Auto-Billing</span>
              <span style={{
                color: billing?.autoBilling ? '#22c55e' : '#999',
                fontWeight: '600'
              }}>
                {billing?.autoBilling ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        {/* Amount Due (if past due or suspended) */}
        {needsPayment && (
          <div className="card" style={{
            padding: '25px',
            background: billing?.isSuspended ? '#fef2f2' : '#fffbeb',
            border: `2px solid ${billing?.isSuspended ? '#fecaca' : '#fde68a'}`
          }}>
            <h2 style={{
              margin: '0 0 20px',
              color: billing?.isSuspended ? '#991b1b' : '#92400e',
              fontSize: '18px'
            }}>
              Amount Due
            </h2>

            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-text-light)' }}>
                  {TIER_NAMES[billing?.subscriptionTier]} Plan
                </span>
                <span>${billing?.monthlyRate?.toFixed(2)}</span>
              </div>

              {billing?.lateFee?.applied && !billing?.lateFee?.waived && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-error)' }}>
                  <span>Late Fee</span>
                  <span>${billing?.lateFee?.amount?.toFixed(2)}</span>
                </div>
              )}

              {billing?.lateFee?.waived && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#22c55e' }}>
                  <span>Late Fee</span>
                  <span style={{ textDecoration: 'line-through' }}>
                    ${billing?.lateFee?.amount?.toFixed(2)} (Waived)
                  </span>
                </div>
              )}

              <div style={{
                borderTop: '2px solid var(--color-border)',
                paddingTop: '12px',
                marginTop: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '20px',
                fontWeight: '700'
              }}>
                <span>Total Due</span>
                <span style={{ color: 'var(--color-error)' }}>${billing?.totalDue?.toFixed(2)}</span>
              </div>

              {billing?.daysPastDue > 0 && (
                <p style={{
                  margin: '10px 0 0',
                  color: 'var(--color-error-text)',
                  fontSize: '14px'
                }}>
                  {billing.daysPastDue} day{billing.daysPastDue !== 1 ? 's' : ''} past due
                </p>
              )}
            </div>

            <button
              onClick={() => setShowPaymentModal(true)}
              style={{
                width: '100%',
                marginTop: '20px',
                padding: '15px',
                borderRadius: '10px',
                border: 'none',
                background: billing?.isSuspended
                  ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                  : 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Pay Now
            </button>
          </div>
        )}

        {/* Payment Method (show if not past due, or as second card) */}
        {!needsPayment && (
          <div className="card" style={{ padding: '25px' }}>
            <h2 style={{ margin: '0 0 20px', color: 'var(--color-brand-text)', fontSize: '18px' }}>
              Payment Method
            </h2>

            {cardDetails ? (
              <div>
                <div style={{
                  background: 'var(--color-surface-sunken)',
                  borderRadius: '10px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    width: '50px',
                    height: '35px',
                    background: '#1a365d',
                    borderRadius: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    {cardDetails.brand}
                  </div>
                  <div>
                    <div style={{ fontWeight: '600' }}>**** **** **** {cardDetails.last4}</div>
                    <div style={{ fontSize: '14px', color: 'var(--color-text-light)' }}>
                      Expires {cardDetails.expMonth}/{cardDetails.expYear}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setShowCardModal(true)}
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                  >
                    Update Card
                  </button>
                  <button
                    onClick={handleRemoveCard}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '1px solid var(--color-error-border)',
                      background: 'var(--color-surface)',
                      color: 'var(--color-error)',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ color: 'var(--color-text-light)', marginBottom: '20px' }}>
                  No payment method on file. Add a card to enable auto-billing.
                </p>
                <button
                  onClick={() => setShowCardModal(true)}
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  Add Payment Method
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Auto-Billing Toggle */}
      <div className="card" style={{ padding: '25px', marginTop: '25px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: '0 0 5px', color: 'var(--color-brand-text)', fontSize: '18px' }}>
              Auto-Billing
            </h2>
            <p style={{ margin: 0, color: 'var(--color-text-light)', fontSize: '14px' }}>
              {billing?.autoBilling
                ? 'Your card will be automatically charged each month'
                : 'You will receive email reminders to pay manually'}
            </p>
          </div>
          <div
            onClick={handleToggleAutoBilling}
            style={{
              position: 'relative',
              display: 'inline-block',
              width: '60px',
              height: '34px',
              cursor: 'pointer'
            }}
          >
            <span style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: billing?.autoBilling ? '#22c55e' : '#ccc',
              transition: '0.4s',
              borderRadius: '34px'
            }}>
              <span style={{
                position: 'absolute',
                height: '26px',
                width: '26px',
                left: billing?.autoBilling ? '30px' : '4px',
                bottom: '4px',
                backgroundColor: 'var(--color-surface)',
                transition: '0.4s',
                borderRadius: '50%'
              }} />
            </span>
          </div>
        </div>
      </div>

      {/* Payment Method (below auto-billing if past due) */}
      {needsPayment && (
        <div className="card" style={{ padding: '25px', marginTop: '25px' }}>
          <h2 style={{ margin: '0 0 20px', color: 'var(--color-brand-text)', fontSize: '18px' }}>
            Payment Method
          </h2>

          {cardDetails ? (
            <div>
              <div style={{
                background: 'var(--color-surface-sunken)',
                borderRadius: '10px',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '50px',
                  height: '35px',
                  background: '#1a365d',
                  borderRadius: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  {cardDetails.brand}
                </div>
                <div>
                  <div style={{ fontWeight: '600' }}>**** **** **** {cardDetails.last4}</div>
                  <div style={{ fontSize: '14px', color: 'var(--color-text-light)' }}>
                    Expires {cardDetails.expMonth}/{cardDetails.expYear}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setShowCardModal(true)}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Update Card
                </button>
                <button
                  onClick={handleRemoveCard}
                  style={{
                    flex: 1,
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-error-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-error)',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ color: 'var(--color-text-light)', marginBottom: '20px' }}>
                No payment method on file. Add a card to enable auto-billing.
              </p>
              <button
                onClick={() => setShowCardModal(true)}
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                Add Payment Method
              </button>
            </div>
          )}
        </div>
      )}

      {/* Payment History */}
      <div className="card" style={{ padding: '25px', marginTop: '25px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: 'var(--color-brand-text)', fontSize: '18px' }}>
            Payment History
          </h2>
          <button
            onClick={handlePrintHistory}
            className="btn btn-secondary"
            style={{ padding: '8px 16px', fontSize: '14px' }}
          >
            Print
          </button>
        </div>

        {paymentHistory.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text-light)', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text-light)', fontWeight: '600' }}>Amount</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text-light)', fontWeight: '600' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'var(--color-text-light)', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: 'var(--color-text-light)', fontWeight: '600' }}>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment) => (
                  <tr key={payment.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '12px' }}>
                      {new Date(payment.payment_date).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </td>
                    <td style={{ padding: '12px', fontWeight: '600' }}>
                      ${parseFloat(payment.amount).toFixed(2)}
                    </td>
                    <td style={{ padding: '12px', textTransform: 'capitalize' }}>
                      {payment.payment_type?.replace('_', ' ') || 'Subscription'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '600',
                        background: payment.status === 'completed' ? '#dcfce7' :
                                    payment.status === 'failed' ? '#fee2e2' : '#fef3c7',
                        color: payment.status === 'completed' ? '#166534' :
                               payment.status === 'failed' ? '#991b1b' : '#92400e'
                      }}>
                        {payment.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {payment.status === 'completed' && (
                        <button
                          onClick={() => handleDownloadReceipt(payment.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-brand-text)',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '14px'
                          }}
                        >
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: 'var(--color-text-light)', textAlign: 'center', padding: '30px' }}>
            No payment history yet.
          </p>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--color-surface)',
            borderRadius: '20px',
            padding: '35px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px', color: 'var(--color-brand-text)' }}>
              Make Payment
            </h2>

            {/* Amount Summary */}
            <div style={{
              background: 'var(--color-surface-sunken)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '25px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span>{TIER_NAMES[billing?.subscriptionTier]} Plan</span>
                <span>${billing?.monthlyRate?.toFixed(2)}</span>
              </div>
              {billing?.lateFee?.applied && !billing?.lateFee?.waived && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-error)', marginBottom: '10px' }}>
                  <span>Late Fee</span>
                  <span>${billing?.lateFee?.amount?.toFixed(2)}</span>
                </div>
              )}
              <div style={{
                borderTop: '2px solid var(--color-border)',
                paddingTop: '10px',
                marginTop: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '18px',
                fontWeight: '700'
              }}>
                <span>Total</span>
                <span style={{ color: 'var(--color-brand-text)' }}>${billing?.totalDue?.toFixed(2)}</span>
              </div>
            </div>

            {/* Card Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>
                Card Information
              </label>
              <div
                ref={cardContainerRef}
                style={{
                  minHeight: '50px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  padding: '12px',
                  background: 'var(--color-surface-sunken)'
                }}
              >
                {!squareLoaded && <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading payment form...</p>}
              </div>
            </div>

            {/* Save Card Checkbox */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '20px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={billing?.autoBilling || false}
                onChange={(e) => setBilling({ ...billing, autoBilling: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <span>Save card for automatic monthly billing</span>
            </label>

            {error && (
              <div style={{
                background: 'var(--color-error-bg)',
                color: 'var(--color-error-text)',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '15px' }}>
              <button
                onClick={() => { setShowPaymentModal(false); setError(''); }}
                style={{
                  flex: 1,
                  padding: '15px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-surface)',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePayNow}
                disabled={processing || !card}
                style={{
                  flex: 2,
                  padding: '15px',
                  borderRadius: '10px',
                  border: 'none',
                  background: processing || !card ? '#ccc' : 'linear-gradient(135deg, var(--color-primary), var(--color-primary-dark))',
                  color: 'white',
                  cursor: processing || !card ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '700'
                }}
              >
                {processing ? 'Processing...' : `Pay $${billing?.totalDue?.toFixed(2)}`}
              </button>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '13px' }}>
              <span style={{ marginRight: '5px' }}>&#128274;</span> Secured by Square
            </div>
          </div>
        </div>
      )}

      {/* Update Card Modal */}
      {showCardModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--color-surface)',
            borderRadius: '20px',
            padding: '35px',
            width: '100%',
            maxWidth: '500px'
          }}>
            <h2 style={{ margin: '0 0 20px', color: 'var(--color-brand-text)' }}>
              {cardDetails ? 'Update Payment Method' : 'Add Payment Method'}
            </h2>

            <p style={{ color: 'var(--color-text-light)', marginBottom: '25px' }}>
              {cardDetails
                ? 'Enter your new card details below.'
                : 'Add a card to enable automatic monthly billing.'}
            </p>

            {/* Card Input */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>
                Card Information
              </label>
              <div
                ref={cardContainerRef}
                style={{
                  minHeight: '50px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  padding: '12px',
                  background: 'var(--color-surface-sunken)'
                }}
              >
                {!squareLoaded && <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading payment form...</p>}
              </div>
            </div>

            {error && (
              <div style={{
                background: 'var(--color-error-bg)',
                color: 'var(--color-error-text)',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '15px' }}>
              <button
                onClick={() => { setShowCardModal(false); setError(''); }}
                style={{
                  flex: 1,
                  padding: '15px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-surface)',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCard}
                disabled={processing || !card}
                style={{
                  flex: 2,
                  padding: '15px',
                  borderRadius: '10px',
                  border: 'none',
                  background: processing || !card ? '#ccc' : 'linear-gradient(135deg, var(--color-primary), var(--color-primary-dark))',
                  color: 'white',
                  cursor: processing || !card ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '700'
                }}
              >
                {processing ? 'Saving...' : (cardDetails ? 'Update Card' : 'Add Card')}
              </button>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '13px' }}>
              <span style={{ marginRight: '5px' }}>&#128274;</span> Secured by Square
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBilling;
